(() => {
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];
  const liveRegion = () => qs('[data-live-region]');

  function announce(message) {
    const region = liveRegion();
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 50);
  }

  function initHeader(root = document) {
    const toggle = qs('[data-mobile-toggle]', root);
    const menu = qs('[data-mobile-menu]', root);
    if (!toggle || !menu || toggle.dataset.ready) return;
    toggle.dataset.ready = 'true';

    const close = () => {
      toggle.setAttribute('aria-expanded', 'false');
      menu.hidden = true;
      document.body.classList.remove('menu-open');
    };

    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(open));
      menu.hidden = !open;
      document.body.classList.toggle('menu-open', open);
    });
    qsa('a', menu).forEach((link) => link.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });
  }

  function initQuantities(root = document) {
    qsa('[data-quantity]', root).forEach((control) => {
      if (control.dataset.ready) return;
      control.dataset.ready = 'true';
      const input = qs('input', control);
      const minus = qs('[data-quantity-minus]', control);
      const plus = qs('[data-quantity-plus]', control);
      if (!input) return;
      minus?.addEventListener('click', () => {
        const minimum = Number(input.min || 0);
        input.value = String(Math.max(minimum, Number(input.value || 0) - 1));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      plus?.addEventListener('click', () => {
        const maximum = Number(input.max || Infinity);
        input.value = String(Math.min(maximum, Number(input.value || 0) + 1));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  function initSort(root = document) {
    qsa('[data-sort-select]', root).forEach((select) => {
      if (select.dataset.ready) return;
      select.dataset.ready = 'true';
      select.addEventListener('change', () => select.form?.submit());
    });
  }

  function updateVariant(root, option) {
    const price = qs('[data-product-price]', root);
    const compare = qs('[data-compare-price]', root);
    const submit = qs('[data-add-to-cart]', root);
    const label = qs('[data-add-label]', root);
    if (price && option.dataset.price) price.textContent = option.dataset.price;
    if (compare) {
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }
    const available = option.dataset.available === 'true';
    if (submit) submit.disabled = !available;
    if (label) label.textContent = available ? 'Ajouter au panier' : 'Épuisé';
  }

  async function updateCartCount() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    qsa('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; });
  }

  function initProductForms(root = document) {
    qsa('[data-product-section]', root).forEach((section) => {
      if (section.dataset.ready) return;
      section.dataset.ready = 'true';
      const form = qs('form.product-form', section);
      const select = qs('[data-variant-select]', section);
      const submit = qs('[data-add-to-cart]', section);
      const label = qs('[data-add-label]', section);
      select?.addEventListener('change', () => updateVariant(section, select.selectedOptions[0]));
      if (!form || !submit) return;

      form.addEventListener('submit', async (event) => {
        if (event.submitter && event.submitter.name !== 'add') return;
        event.preventDefault();
        submit.disabled = true;
        if (label) label.textContent = 'Ajout en cours…';
        try {
          const rootUrl = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${rootUrl}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || 'Impossible d’ajouter ce produit.');
          await updateCartCount();
          if (label) label.textContent = 'Ajouté au panier ✓';
          announce(`${data.product_title || 'Le produit'} a été ajouté au panier.`);
          window.setTimeout(() => {
            submit.disabled = false;
            if (label) label.textContent = 'Ajouter au panier';
          }, 1700);
        } catch (error) {
          submit.disabled = false;
          if (label) label.textContent = 'Ajouter au panier';
          announce(error.message || 'Une erreur est survenue.');
        }
      });
    });
  }

  function initDiagnostic(root = document) {
    qsa('[data-diagnostic]', root).forEach((card) => {
      if (card.dataset.ready) return;
      card.dataset.ready = 'true';
      const steps = qsa('[data-step]', card);
      const result = qs('[data-diagnostic-result]', card);
      const next = qs('[data-diagnostic-next]', card);
      const back = qs('[data-diagnostic-back]', card);
      const progress = qs('[data-diagnostic-progress]', card);
      const bar = qs('[data-diagnostic-bar]', card);
      let current = 0;

      const selected = (name) => qs(`input[name="${name}"]:checked`, card)?.value || '';
      const names = ['building', 'need', 'timeline'];

      const render = () => {
        steps.forEach((step, index) => {
          const active = index === current;
          step.hidden = !active;
          step.classList.toggle('is-active', active);
        });
        if (result) result.hidden = true;
        if (next) {
          next.hidden = false;
          next.disabled = !selected(names[current]);
          next.textContent = current === steps.length - 1 ? 'Voir ma recommandation' : 'Continuer';
        }
        if (back) back.hidden = current === 0;
        if (progress) progress.textContent = `${current + 1} / ${steps.length}`;
        if (bar) bar.style.width = `${((current + 1) / steps.length) * 100}%`;
      };

      const showResult = () => {
        const building = selected('building');
        const need = selected('need');
        const timeline = selected('timeline');
        const title = qs('[data-result-title]', card);
        const copy = qs('[data-result-copy]', card);
        const email = qs('[data-result-email]', card);
        let recommendation = 'Un conseiller SPIQ peut confirmer la prochaine étape adaptée à votre situation.';
        let heading = 'Parlons de votre besoin';

        if (need === 'Inspection ou entretien') {
          heading = 'Planifions une première évaluation';
          recommendation = 'Une courte validation de la portée nous permettra de préparer une demande de service claire.';
        } else if (need === 'Extincteur ou équipement' || need === 'Détecteur ou avertisseur') {
          heading = 'Vérifions le bon équipement';
          recommendation = 'Le type de bâtiment et l’usage prévu doivent être confirmés avant de choisir le produit.';
        } else if (need === 'Conseil pour choisir') {
          heading = 'Obtenez une recommandation personnalisée';
          recommendation = 'Transmettez-nous quelques détails et nous vous orienterons vers le service ou le produit pertinent.';
        }

        if (title) title.textContent = heading;
        if (copy) copy.textContent = recommendation;
        if (email) {
          const subject = `Demande SPIQ — ${need}`;
          const body = `Bonjour SPIQ,\n\nVoici ma demande :\n• Bâtiment : ${building}\n• Besoin : ${need}\n• Délai : ${timeline}\n\nMes coordonnées :\nNom : \nTéléphone : \nAdresse du bâtiment : \n\nMerci.`;
          email.href = `mailto:${card.dataset.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        }
        steps.forEach((step) => { step.hidden = true; });
        if (result) result.hidden = false;
        if (next) next.hidden = true;
        if (back) back.hidden = false;
        if (progress) progress.textContent = 'Terminé';
        if (bar) bar.style.width = '100%';
        result?.focus?.();
        announce('Votre recommandation est prête.');
      };

      qsa('input[type="radio"]', card).forEach((input) => {
        input.addEventListener('change', () => { if (next) next.disabled = false; });
      });
      next?.addEventListener('click', () => {
        if (current < steps.length - 1) { current += 1; render(); }
        else showResult();
      });
      back?.addEventListener('click', () => {
        if (result && !result.hidden) { result.hidden = true; current = steps.length - 1; render(); return; }
        current = Math.max(0, current - 1);
        render();
      });
      render();
    });
  }

  function init(root = document) {
    initHeader(root);
    initQuantities(root);
    initSort(root);
    initProductForms(root);
    initDiagnostic(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
